import { PhoneCall, SimCard, WarningCircle } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import PhoneInput from "../../../components/SearchablePhoneInput";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { formatAsMoney } from "../../../helpers/numbers";
import { getFriendlyBillsErrorMessage } from "../../../helpers/billsErrors";
import useBillAirtimeProviders, {
  BillNetwork,
} from "../../../hooks/useBillAirtimeProviders";
import useBillAirtimePurchase, {
  BillAirtimePurchasePayload,
} from "../../../hooks/useBillAirtimePurchase";
import BillPurchaseSuccess from "./BillPurchaseSuccess";
import Confirm from "../Confirm";
import BillPageHeader from "./components/BillPageHeader";
import SelectField from "./components/SelectField";
import ProviderSelectModal from "./components/ProviderSelectModal";
import BillSummaryCard from "./components/BillSummaryCard";
import SetPinPrompt from "./components/SetPinPrompt";

const WALLET_PAYMENT_OPTION_CODE = "004";
const MINIMUM_AIRTIME_AMOUNT = 50;
const MAXIMUM_AIRTIME_AMOUNT = 5000;
const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    return Number(value.replace(/[^\d.-]/g, "")) || 0;
  }

  return 0;
};

const generateAgentReference = () =>
  `airt_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const AirtimeTemp = () => {
  const { user, refetch } = useAuth();

  const [selectedProvider, setSelectedProvider] = useState<BillNetwork | null>(
    null
  );
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [pin, setPin] = useState("");
  const [payload, setPayload] = useState<BillAirtimePurchasePayload | null>(
    null
  );
  const [providerModalOpen, setProviderModalOpen] = useState(false);

  const { providers, fetchingProviders, isProvidersError, providersError } =
    useBillAirtimeProviders();

  const {
    purchaseAirtime,
    purchasingAirtime,
    isPurchaseError,
    purchaseError,
    purchaseResponse,
  } = useBillAirtimePurchase({
    onSuccess(data) {
      toast.success(data?.message || "Airtime purchased successfully");
      refetch();
      setConfirming(false);
    },
    onError(error) {
      toast.error(getFriendlyBillsErrorMessage(error, "airtime"));
    },
  });

  const amountNumber = useMemo(() => Number(amount || 0), [amount]);
  const availableBalance = toNumber(user?.wallet?.available_balance);
  const isAmountInRange =
    amountNumber >= MINIMUM_AIRTIME_AMOUNT &&
    amountNumber <= MAXIMUM_AIRTIME_AMOUNT;
  const hasEnoughBalance = availableBalance >= amountNumber;
  const canProceed =
    !!selectedProvider &&
    !!phoneNumber &&
    isAmountInRange &&
    hasEnoughBalance &&
    !purchasingAirtime;
  const purchaseErrorMessage = getFriendlyBillsErrorMessage(
    purchaseError,
    "airtime"
  );

  const handleAmountChange = (value: string) => {
    const normalized = value.replace(/[^\d]/g, "");
    setAmount(normalized);
  };

  const handleSubmit = () => {
    if (!user?.pin_status) {
      return;
    }

    if (!selectedProvider || !phoneNumber || !amountNumber) {
      toast.error("Select a network, enter a phone number, and add an amount.");
      return;
    }

    if (!isAmountInRange) {
      toast.error("Airtime amount must be between NGN 50 and NGN 5,000.");
      return;
    }

    if (!hasEnoughBalance) {
      toast.error("Insufficient wallet balance.");
      return;
    }

    setPayload({
      pin: "",
      phone: phoneNumber,
      service_type: selectedProvider.code,
      amount: amountNumber,
      payment_option_code: WALLET_PAYMENT_OPTION_CODE,
      agentReference: generateAgentReference(),
    });
    setConfirming(true);
  };

  const authorizePurchase = useCallback(() => {
    if (!payload || !pin) {
      return;
    }

    purchaseAirtime({
      ...payload,
      pin,
    });
  }, [payload, pin, purchaseAirtime]);

  if (!user?.pin_status) {
    return (
      <SetPinPrompt
        redirect="/payments/bills/airtime"
        action="buying airtime"
      />
    );
  }

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      {(fetchingProviders || purchasingAirtime) && (
        <div className="fixed top-0 z-[120] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {purchaseResponse && (
        <BillPurchaseSuccess
          title="Airtime purchase successful!"
          message={
            <>
              Congratulations! You have successfully purchased{" "}
              <span className="font-bold">
                NGN {formatAsMoney(amountNumber)}
              </span>{" "}
              airtime for <span className="font-bold">{phoneNumber}</span>.
            </>
          }
        />
      )}

      {confirming && selectedProvider && (
        <Confirm
          title="Confirm Airtime Purchase"
          setConfirming={setConfirming}
          onAuthorized={authorizePurchase}
          onPinChange={setPin}
          description={
            <div>
              You are about to buy{" "}
              <span className="font-bold">
                NGN {formatAsMoney(amountNumber)}
              </span>{" "}
              {selectedProvider.name} airtime for{" "}
              <span className="font-bold">{phoneNumber}</span>.
            </div>
          }
        />
      )}

      <div className="mx-auto max-w-5xl flex flex-col gap-6">
        <BillPageHeader
          title="Buy Airtime"
          subtitle="Top up any Nigerian line from your wallet."
          icon={PhoneCall}
          walletBalance={availableBalance}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <section className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col gap-5">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Recipient phone number
              </label>
              <PhoneInput
                placeholder="Enter phone number"
                international
                defaultCountry="NG"
                withCountryCallingCode
                value={phoneNumber}
                onChange={setPhoneNumber}
                className="mt-2 focus:outline-none flex-1 flex items-center rounded-xl py-2 border border-gray-300 shadow-sm px-4 w-full focus-within:outline-deepBlue focus-within:outline"
              />
            </div>

            <SelectField
              label="Network"
              placeholder="Select network"
              icon={SimCard}
              value={selectedProvider?.name}
              onClick={() => setProviderModalOpen(true)}
              loading={fetchingProviders}
              loadingText="Loading networks..."
            />

            <div>
              <label className="text-sm font-medium text-gray-700">
                Amount
              </label>
              <div className="mt-2 rounded-xl border border-gray-300 shadow-sm px-4 py-3 flex items-center gap-2 focus-within:outline focus-within:outline-deepBlue">
                <span className="text-gray-500">NGN</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={(event) => handleAmountChange(event.target.value)}
                  placeholder="Enter amount"
                  className="w-full outline-none"
                />
              </div>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2">
                {QUICK_AMOUNTS.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    onClick={() => setAmount(String(quickAmount))}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                      amountNumber === quickAmount
                        ? "border-visaro-orange bg-visaro-orange/5 text-visaro-orange"
                        : "border-gray-200 hover:border-visaro-orange/60"
                    }`}
                  >
                    {formatAsMoney(quickAmount)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Airtime purchases are between NGN 50 and NGN 5,000.
              </p>
            </div>

            {isProvidersError && (
              <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                {providersError?.message || "Unable to load providers."}
              </div>
            )}
          </section>

          <BillSummaryCard
            rows={[
              { label: "Network", value: selectedProvider?.name || "Not selected" },
              { label: "Phone", value: phoneNumber || "Not entered" },
            ]}
            amount={amountNumber}
            canProceed={canProceed}
            onProceed={handleSubmit}
            alerts={
              <>
                {!!amountNumber && !isAmountInRange && (
                  <div className="rounded-xl bg-yellow-50 text-yellow-700 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>
                      Airtime amount must be between NGN 50 and NGN 5,000.
                    </span>
                  </div>
                )}
                {!!amountNumber && !hasEnoughBalance && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>
                      Your wallet balance is not enough for this airtime
                      purchase.
                    </span>
                  </div>
                )}
                {isPurchaseError && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                    {purchaseErrorMessage}
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>

      <ProviderSelectModal
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        providers={providers}
        selectedCode={selectedProvider?.code}
        onSelect={setSelectedProvider}
        label="network"
        icon={SimCard}
      />
    </div>
  );
};

export default AirtimeTemp;
